/**
 * @file context/orderContext.tsx
 *
 * This file defines the OrderContext and OrderProvider, which manage the state of an order in the application.
 * It uses React's Context API and useReducer hook to manage and persist order state.
 */

'use client';

// Core & Package Imports
import React, { createContext, useEffect, useReducer } from 'react';

// Type Imports
import { OrderState } from '@/lib/types/orderState';

// Context Imports
import { OrderReducer } from '@/lib/context/orderReducer';
import { OrderActions } from '@/lib/context/orderActions';

// Context Defaults
import { defaultOrderState } from '@/lib/context/orderDefaults';

import { getCookie } from '@/lib/functions/getCookie';
import { getOrderStateCookieKey, persistOrderState } from '@/lib/functions/persistOrderState';

const LEGACY_LOCAL_STORAGE_KEY = 'OrderState';

export const OrderContext = createContext<{
  state: OrderState;
  dispatch: React.Dispatch<OrderActions>;
} | null>(null);

export const useOrder = () => {
  const user = React.useContext(OrderContext);
  if (!user) {
    throw new Error('useOrder must be used within an OrderContext.');
  }
  return user;
};

function readPersistedOrderState(): OrderState | null {
  if (typeof document === 'undefined') {
    return null;
  }

  const persisted = getCookie(getOrderStateCookieKey());
  if (persisted) {
    try {
      return JSON.parse(persisted) as OrderState;
    } catch {
      // malformed cookie; fall through to legacy migration
    }
  }

  try {
    const legacy = localStorage.getItem(LEGACY_LOCAL_STORAGE_KEY);
    if (legacy) {
      const parsed = JSON.parse(legacy) as OrderState;
      localStorage.removeItem(LEGACY_LOCAL_STORAGE_KEY);
      persistOrderState(parsed);
      return parsed;
    }
  } catch {
    // no-op
  }

  return null;
}

const OrderProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(OrderReducer, defaultOrderState, (initial) => {
    return readPersistedOrderState() ?? initial;
  });

  // Persist order state in cookie (24h expiry; resets on every update)
  useEffect(() => {
    persistOrderState(state);
  }, [state]);

  return (
    <OrderContext.Provider value={{ state, dispatch }}>
      {children}
    </OrderContext.Provider>
  );
};

export default OrderProvider;
